import { Controller } from "@hotwired/stimulus"
import { loadYandexMaps, boundaryBbox, boundaryCenter, createDistrictPolygon } from "../lib/yandex_maps"

export default class extends Controller {
  static values = {
    apikey: String,
    lat: Number,
    lng: Number,
    districtCenterLat: Number,
    districtCenterLng: Number,
    boundary: { type: Object, default: null },
    districtName: { type: String, default: "районе" }
  }
  static targets = [
    "container", "lat", "lng", "settlement", "address", "addressDisplay",
    "addressIcon", "addressText", "locationButton", "locationIcon", "locationSpinner",
    "searchInput", "searchButton", "searchIcon", "searchSpinner",
    "searchClearButton", "searchMessage", "searchContainer"
  ]

  connect() {
    if (!this.apikeyValue) return

    this.hideLocationButtonIfUnavailable()
    loadYandexMaps(this.apikeyValue).then(() => this.initMap())
  }

  initMap() {
    const center = this.initialCenter()
    const zoom = this.initialZoom()

    this.map = new ymaps.Map(this.containerTarget, {
      center: center,
      zoom: zoom
    })

    this.map.controls.remove("searchControl")
    this.map.controls.remove("geolocationControl")
    this.containerTarget.map = this.map

    this.drawBoundary()
    this.setRestrictMapArea()

    if (this.latValue && this.lngValue) {
      this.setMarker(this.latValue, this.lngValue)
      this.updateAddressDisplayFromField()
    } else {
      this.centerOnSelectedSettlement()
      this.showAddressPlaceholder()
    }

    if (this.containerTarget.classList.contains("border-red-500")) {
      this.showMapStatus("Укажите точку на карте", "error")
    }

    this.map.events.add("click", (event) => {
      const coords = event.get("coords")
      this.handleMapClick(coords[0], coords[1])
    })

    this.containerTarget.simulateMapClick = (lat, lng) => this.handleMapClick(lat, lng)
  }

  handleMapClick(lat, lng) {
    if (!this.isInsideBoundary(lat, lng)) {
      this.showMapStatus("Можно ставить точку только в пределах района", "error")
      return
    }

    this.clearMapError()
    this.setMarker(lat, lng)
    this.latTarget.value = lat
    this.lngTarget.value = lng
    this.fillAddress(lat, lng)
  }

  initialCenter() {
    if (this.latValue && this.lngValue) return [ this.latValue, this.lngValue ]
    return boundaryCenter(this.boundaryValue) || [ this.districtCenterLatValue || 55.7558, this.districtCenterLngValue || 37.6173 ]
  }

  initialZoom() {
    if (this.latValue && this.lngValue) return 15
    return this.boundaryValue ? 10 : 11
  }

  drawBoundary() {
    this.districtPolygon = createDistrictPolygon(this.boundaryValue)
    if (!this.districtPolygon) return

    this.map.geoObjects.add(this.districtPolygon)
  }

  setRestrictMapArea() {
    const bbox = this.expandedBoundaryBbox()
    if (!bbox) return

    this.map.options.set("restrictMapArea", bbox)
  }

  expandedBoundaryBbox() {
    const bbox = boundaryBbox(this.boundaryValue)
    if (!bbox) return null

    const centerLat = (bbox[0][0] + bbox[1][0]) / 2
    const km = 5
    const latMargin = km / 111.0
    const lngMargin = km / (111.0 * Math.cos(centerLat * Math.PI / 180))

    return [
      [ bbox[0][0] - latMargin, bbox[0][1] - lngMargin ],
      [ bbox[1][0] + latMargin, bbox[1][1] + lngMargin ]
    ]
  }

  isInsideBoundary(lat, lng) {
    if (!this.boundaryValue) return true

    return this.pointInBoundary(lat, lng, this.boundaryValue)
  }

  pointInBoundary(lat, lng, geoJson) {
    const point = [ lng, lat ]

    if (geoJson.type === "Polygon") {
      return this.polygonContainsPoint(point, geoJson.coordinates)
    }
    if (geoJson.type === "MultiPolygon") {
      return geoJson.coordinates.some((polygon) => this.polygonContainsPoint(point, polygon))
    }
    return false
  }

  polygonContainsPoint(point, rings) {
    if (!this.ringContainsPoint(point, rings[0])) return false

    for (let i = 1; i < rings.length; i++) {
      if (this.ringContainsPoint(point, rings[i])) return false
    }
    return true
  }

  ringContainsPoint(point, ring) {
    const x = point[0]
    const y = point[1]
    let inside = false
    let j = ring.length - 1

    for (let i = 0; i < ring.length; i++) {
      const xi = ring[i][0]
      const yi = ring[i][1]
      const xj = ring[j][0]
      const yj = ring[j][1]

      if ((yi > y) !== (yj > y) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside
      }
      j = i
    }

    return inside
  }

  settlementChanged() {
    if (!this.map) return

    this.centerOnSelectedSettlement()
  }

  centerOnSelectedSettlement() {
    if (!this.hasSettlementTarget) return

    const option = this.settlementTarget.selectedOptions[0]
    if (!option) return

    const lat = parseFloat(option.dataset.lat)
    const lng = parseFloat(option.dataset.lng)
    if (Number.isNaN(lat) || Number.isNaN(lng)) return

    this.map.setCenter([ lat, lng ], 13)
  }

  hideLocationButtonIfUnavailable() {
    if (!this.hasLocationButtonTarget) return
    if (!navigator.geolocation) this.locationButtonTarget.classList.add("hidden")
  }

  locateMe(event) {
    event.preventDefault()
    if (!navigator.geolocation) return

    this.setLocationLoading(true)
    this.clearMapError()

    navigator.geolocation.getCurrentPosition(
      (position) => this.handlePosition(position),
      (error) => this.handlePositionError(error),
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  handlePosition(position) {
    const lat = position.coords.latitude
    const lng = position.coords.longitude

    if (!this.isInsideBoundary(lat, lng)) {
      this.setLocationLoading(false)
      this.showMapStatus("Можно ставить точку только в пределах района", "error")
      return
    }

    this.map.setCenter([ lat, lng ], 16)
    this.setMarker(lat, lng)
    this.latTarget.value = lat
    this.lngTarget.value = lng
    this.fillAddress(lat, lng)
    this.setLocationLoading(false)
  }

  handlePositionError(_error) {
    this.setLocationLoading(false)
    this.showMapStatus("Не удалось определить местоположение. Разрешите доступ к геолокации или поставьте точку вручную.", "error")
  }

  setLocationLoading(isLoading) {
    if (!this.hasLocationButtonTarget) return

    this.locationButtonTarget.disabled = isLoading
    if (this.hasLocationIconTarget) {
      this.locationIconTarget.classList.toggle("hidden", isLoading)
    }
    if (this.hasLocationSpinnerTarget) {
      this.locationSpinnerTarget.classList.toggle("hidden", !isLoading)
    }
  }

  setAddress(address) {
    if (!this.hasAddressTarget) return

    if (address) {
      this.addressTarget.value = address
      this.showMapStatus(address, "address")
    } else {
      this.addressTarget.value = ""
      this.showMapStatus("Точный адрес не найден", "not_found")
    }
  }

  updateAddressDisplayFromField() {
    if (!this.hasAddressTarget || !this.hasAddressDisplayTarget) return

    const address = this.addressTarget.value.trim()
    if (address) {
      this.showMapStatus(address, "address")
    } else if (this.latTarget.value && this.lngTarget.value) {
      this.showMapStatus("Точный адрес не найден", "not_found")
    } else {
      this.showAddressPlaceholder()
    }
  }

  showMapStatus(text, state) {
    if (!this.hasAddressDisplayTarget || !this.hasAddressTextTarget) return

    this.addressTextTarget.textContent = text

    let colorClass = "text-gray-400"
    let showIcon = false

    if (state === "address") {
      colorClass = "text-gray-700"
      showIcon = true
    } else if (state === "error") {
      colorClass = "text-red-600"
    }

    this.addressDisplayTarget.className = `min-h-[1.5rem] text-sm ${colorClass} flex items-center gap-1.5 mt-2`

    if (this.hasAddressIconTarget) {
      this.addressIconTarget.classList.toggle("hidden", !showIcon)
    }
  }

  showAddressPlaceholder() {
    this.showMapStatus("Нажмите на карту — адрес подтянется автоматически", "placeholder")
  }

  clearMapError() {
    this.containerTarget.classList.remove("border-red-500")
    this.containerTarget.classList.add("border-gray-300")
  }

  setMarker(lat, lng) {
    if (this.marker) {
      this.map.geoObjects.remove(this.marker)
    }

    this.marker = new ymaps.Placemark([ lat, lng ], {}, {
      preset: "islands#redDotIcon"
    })

    this.map.geoObjects.add(this.marker)
  }

  fillAddress(lat, lng) {
    if (!this.hasAddressTarget) return

    this.geocodeAddress(lat, lng).then((address) => {
      this.setAddress(address)
    }).catch(() => {
      this.setAddress(null)
    })
  }

  async geocodeAddress(lat, lng) {
    let data = await this.tryGeocode(lat, lng, "house")
    if (!data?.shortAddress) data = await this.tryGeocode(lat, lng, "street")
    if (!data?.shortAddress) data = await this.tryGeocode(lat, lng, null)

    if (data?.shortAddress) return data.shortAddress

    const nearest = await this.tryNearestLocality(lat, lng)
    return nearest ? `Рядом: ${nearest}` : null
  }

  async tryNearestLocality(lat, lng) {
    try {
      const res = await fetch(`/geocode/nearest_locality?lat=${lat}&lng=${lng}`)
      if (!res.ok) return null
      const data = await res.json()
      return data?.name || null
    } catch {
      return null
    }
  }

  tryGeocode(lat, lng, kind) {
    if (!this.apikeyValue) return Promise.resolve(null)

    const params = new URLSearchParams({
      apikey: this.apikeyValue,
      geocode: `${lng},${lat}`,
      format: "json",
      results: "1",
      lang: "ru_RU"
    })
    if (kind) params.set("kind", kind)

    return fetch(`https://geocode-maps.yandex.ru/1.x/?${params.toString()}`)
      .then((res) => {
        if (!res.ok) return null
        return res.json()
      })
      .then((data) => {
        const geoObject = data?.response?.GeoObjectCollection?.featureMember?.[0]?.GeoObject
        if (!geoObject) return null

        return {
          shortAddress: this.formatAddress(geoObject),
          raw: geoObject
        }
      })
      .catch(() => null)
  }

  formatAddress(geoObject) {
    const components = geoObject?.metaDataProperty?.GeocoderMetaData?.Address?.Components || []
    const byKind = {}
    components.forEach((component) => { byKind[component.kind] = component.name })

    const locality = byKind["locality"]
    const street = byKind["street"]
    const house = byKind["house"]

    if (locality) {
      const parts = [ street, house ].filter(Boolean)
      if (parts.length > 0) {
        return `${parts.join(", ")}, ${locality}`
      }
      return locality
    }

    return null
  }

  searchKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.search()
    }
  }

  async search() {
    if (!this.hasSearchInputTarget || !this.map) return

    const query = this.searchInputTarget.value.trim()
    if (!query) return

    this.setSearchLoading(true)
    this.clearSearchMessage()

    try {
      const params = new URLSearchParams({
        apikey: this.apikeyValue,
        geocode: query,
        format: "json",
        results: "5",
        lang: "ru_RU"
      })

      const bbox = boundaryBbox(this.boundaryValue)
      if (bbox) {
        params.set("bbox", `${bbox[0][1]},${bbox[0][0]}~${bbox[1][1]},${bbox[1][0]}`)
        params.set("rspn", "1")
      }

      const res = await fetch(`https://geocode-maps.yandex.ru/1.x/?${params.toString()}`)
      this.setSearchLoading(false)

      if (!res.ok) {
        this.showSearchMessage("Не удалось выполнить поиск. Попробуйте позже.", "error")
        return
      }

      const data = await res.json()
      this.handleSearchResult(data)
    } catch (error) {
      this.setSearchLoading(false)
      this.showSearchMessage("Не удалось выполнить поиск. Попробуйте позже.", "error")
      console.error("Yandex geocode fetch error:", error)
    }
  }

  handleSearchResult(data) {
    const geoObject = data?.response?.GeoObjectCollection?.featureMember?.[0]?.GeoObject

    if (!geoObject) {
      this.showSearchMessage(`В ${this.districtNameValue} не найдено, уточните запрос`, "error")
      return
    }

    const [ lng, lat ] = geoObject.Point.pos.split(" ").map(parseFloat)
    const precision = geoObject?.metaDataProperty?.GeocoderMetaData?.precision

    const approximatePrecisions = [ "street", "other", "near", "range", "locality", "district", "area", "province", "country" ]
    const isApproximate = approximatePrecisions.includes(precision)

    this.map.setCenter([ lat, lng ], 16)
    this.handleMapClick(lat, lng)

    if (isApproximate) {
      this.showSearchMessage("Найдено примерное местоположение. Уточните адрес до дома", "warning")
    } else {
      this.clearSearchMessage()
    }
  }

  searchInputChanged() {
    if (!this.hasSearchClearButtonTarget) return

    const hasValue = this.searchInputTarget.value.length > 0
    this.searchClearButtonTarget.classList.toggle("hidden", !hasValue)
  }

  clearSearch() {
    if (!this.hasSearchInputTarget) return

    this.searchInputTarget.value = ""
    this.searchInputTarget.focus()
    this.searchInputChanged()
    this.clearSearchMessage()
  }

  setSearchLoading(isLoading) {
    if (!this.hasSearchButtonTarget) return

    this.searchButtonTarget.disabled = isLoading
    if (this.hasSearchIconTarget) {
      this.searchIconTarget.classList.toggle("hidden", isLoading)
    }
    if (this.hasSearchSpinnerTarget) {
      this.searchSpinnerTarget.classList.toggle("hidden", !isLoading)
    }
  }

  showSearchMessage(text, state) {
    if (!this.hasSearchMessageTarget) return

    this.searchMessageTarget.textContent = text
    this.searchMessageTarget.className = "text-xs mt-1 min-h-[1.25rem] " + this.searchMessageColorClass(state)
  }

  clearSearchMessage() {
    this.showSearchMessage("", "default")
  }

  searchMessageColorClass(state) {
    switch (state) {
      case "error": return "text-red-600"
      case "warning": return "text-amber-600"
      case "success": return "text-green-600"
      default: return "text-gray-500"
    }
  }
}
